import React, { useMemo } from 'react';
import { Drawer, LoadingOverlay } from '@mantine/core';
import { useQuery } from '@apollo/client';

import { GET_USER } from '../../apollo/queries/user';
import { User, UserNumber } from '../../codegen/graphql';
import UserForm from './UserForm';
import { useUserPortal } from '../../context/UserPortalContext';
import { mapContactNumber } from '../../utility/mapContactNumber';

function UserSidesheet() {
    const { user, updateUser, userSidesheet, closeUserSidesheet } =
        useUserPortal();

    const { data: userData, loading: loadingUserData } = useQuery(GET_USER, {
        variables: { id: user?.id },
        skip: !user?.id,
    });

    const formUser = useMemo(() => {
        let targetUser: User | null = null;

        targetUser = userData?.getUserById ?? user;

        if (!targetUser) {
            return null;
        }

        return {
            designation: targetUser?.designation,
            email: targetUser?.email,
            faxNo: targetUser?.faxNo,
            firstName: targetUser?.firstName ?? '',
            id: targetUser?.id ?? '',
            isActive: targetUser?.isActive,
            lastName: targetUser?.lastName ?? '',
            managerStoreId: targetUser?.managerStore?.id,
            mobileNo: mapContactNumber(targetUser?.mobileNo as UserNumber),
            sapUserId: targetUser?.sapUser?.id,
            sendInvite: false,
            speedDial: targetUser?.speedDial,
            telNo: mapContactNumber(targetUser?.telNo as UserNumber),
            userRoleId: targetUser?.userRole?.id ?? '',
            username: targetUser?.username,
        };
    }, [userData, user]);

    const title = useMemo(() => {
        if (loadingUserData) {
            return 'Loading...';
        }

        if (formUser) {
            return `Edit User: ${formUser.firstName} ${formUser.lastName}`;
        }
        return 'Add User';
    }, [formUser, loadingUserData]);

    return (
        <Drawer
            opened={userSidesheet.opened}
            onClose={closeUserSidesheet}
            title={title}
            classNames={{
                title: 'text-primary font-semibold text-lg',
            }}
            size={700}
            keepMounted={false}
        >
            {loadingUserData ? (
                <LoadingOverlay visible zIndex={1000} />
            ) : (
                <UserForm
                    initialValues={formUser}
                    isEditing={!!formUser}
                    onClose={closeUserSidesheet}
                    updateUser={updateUser}
                />
            )}
        </Drawer>
    );
}

export default UserSidesheet;
